// Comment.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { formatDateTime } from 'utilities/DateTime';
import { getActivityCommentKey } from 'node/activity/utils/activityReactions';

// CSS
import './Comment.css';

// Components
import CommentActions from './actions/CommentActions';
import CommentBottomToggle from './bottomtoggle/CommentBottomToggle';
import CommentFooter from './footer/CommentFooter';
import CommentReply from './reply/CommentReply';
import CommentTopToggle from './toptoggle/CommentTopToggle';
import ActivityCommentAttachments from 'node/activity/items/ActivityCommentAttachments';
import ActivityReactionControl from 'node/activity/items/ActivityReactionControl';
import ProfilePhoto from 'components/profilephoto/ProfilePhoto';

/**
 * Comment component
 *
 * Renders a comment.
 *
 * @param {object} props
 * @param {'datetime'|'time'} [props.timestampStyle] - Full date+time, or time only.
 * @returns {ReactNode} The rendered component.
 */
const Comment = ({
    node,
    comment,
    comments,
    commentLikes = [],
    reactions,
    replies,
    repliesCount,
    hasMoreReplies,
    showReplies,
    displayedReplies,
    onToggleReplies,
    onToggleMoreReplies,
    onReplyClicked,
    onDeleteClicked,
    timestampStyle = 'datetime',
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    const commentUser = {
        ...(comment?.user || {}),
        key: comment?.user?.key || comment?.userKey || null,
        userKey: comment?.user?.userKey || comment?.userKey || null,
        name: comment?.user?.name || comment?.name || '',
        coverUrl: comment?.user?.coverUrl || comment?.coverUrl || null,
        coverStoragePath: comment?.user?.coverStoragePath || comment?.coverStoragePath || comment?.user?.storagePath || comment?.storagePath || null,
        storagePath: comment?.user?.storagePath || comment?.storagePath || comment?.user?.coverStoragePath || comment?.coverStoragePath || null,
    };

    const commentReactions = useMemo(() => {
      const commentKey = getActivityCommentKey(comment);
      const rows = Array.isArray(reactions) ? reactions : commentLikes;
      return rows.filter((row) => String(row?.commentKey || '').trim() === commentKey);
    }, [comment, commentLikes, reactions]);

    const timestampLabel = useMemo(() => {
        const raw = comment?.dateCreated;
        if (!raw) return '';
        const date = typeof raw?.toDate === 'function' ? raw.toDate() : new Date(raw);
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
        return formatDateTime(
            date,
            timestampStyle === 'time'
                ? { intlOptions: { hour: 'numeric', minute: '2-digit', hour12: true } }
                : undefined
        );
    }, [comment?.dateCreated, timestampStyle]);

    // ----- RENDER ----- //

    return (
        <>
            <div className="comment-container" key={comment.key}>

                {/* WRAPPER */}
                <div className="comment-profile-photo-wrapper">
                    <ProfilePhoto
                      node={commentUser}
                      size={30}
                      borderWidth={1}
                    />
                </div>

                {/* RIGHT */}
                <div className="comment-right">

                    {/* NAME + REACTIONS + DATE + DELETE */}
                    <div className="comment-header">
                        <div className="comment-header-meta">
                            <div
                              className={comment.deleted ? "comment-name-deleted" : "comment-name"}
                              style={{
                                color: theme.foregroundMuted,
                              }}
                            >
                                {commentUser.name}
                            </div>
                        </div>
                        <div className="comment-header-end">
                            <ActivityReactionControl
                              comment={comment}
                              node={node}
                              reactions={commentReactions}
                            />
                            {timestampLabel ? (
                                <div className="comment-timestamp">
                                    {timestampLabel}
                                </div>
                            ) : null}
                            <CommentActions
                              comment={comment}
                              onDeleteClicked={onDeleteClicked}
                            />
                        </div>
                    </div>

                    {/* TEXT DELETED */}
                    <div
                      className={comment.deleted ? "comment-text-deleted" : "comment-text"}
                      style={{
                        color: comment.deleted ? theme.foregroundMuted : theme.foreground,
                      }}
                    >
                        {comment.text}
                    </div>

                    <ActivityCommentAttachments
                      attachments={comment.attachments}
                      deleted={Boolean(comment.deleted)}
                    />

                    {/* FOOTER */}
                    <div className="comment-footer">
                        <CommentFooter
                          node={node}
                          comment={comment}
                          onReplyClicked={onReplyClicked}
                        />
                    </div>

                    {repliesCount > 0 && !showReplies[comment.key] && (
                        <CommentTopToggle
                          repliesCount={repliesCount}
                          onClick={() => onToggleReplies(comment.key)}
                        />
                    )}

                    {/* REPLIES */}
                    {showReplies[comment.key] && (

                        <div className="comment-replies">

                            {displayedReplies.map(reply => (

                                <CommentReply
                                  key={reply.key}
                                  node={node}
                                  reply={reply}
                                  comment={comment}
                                  comments={comments}
                                  reactions={Array.isArray(reactions) ? reactions : commentLikes}
                                  onDeleteClicked={onDeleteClicked}
                                  onReplyClicked={onReplyClicked}
                                />

                            ))}

                            <CommentBottomToggle
                              comment={comment}
                              hasMoreReplies={hasMoreReplies}
                              showReplies={showReplies}
                              replies={replies}
                              displayedReplies={displayedReplies}
                              onExpand={() => onToggleMoreReplies(comment.key)}
                              onHide={() => onToggleReplies(comment.key)}
                            />

                        </div>
                    )}

                </div>

            </div>
        </>
    );
};

// Dev Tools
Comment.displayName = 'Comment';

export default Comment;
